import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { QuillEditorComponent, type QuillModules } from 'ngx-quill';

export const WYSIWYG_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => WysiwygComponent),
  multi: true,
};

/**
 * The overloading of the styling in the Quill library is done globally in the file lib/scss/_wysiwyg.scss
 */
@Component({
  selector: 'myre-wysiwyg',
  templateUrl: './wysiwyg.component.html',
  styleUrls: ['./wysiwyg.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WYSIWYG_VALUE_ACCESSOR],
  imports: [QuillEditorComponent, FormsModule],
})
export class WysiwygComponent implements ControlValueAccessor {
  private _cdr = inject(ChangeDetectorRef);

  @Input()
  public height: number = 120;

  @Input()
  public placeholder: string;

  public disabled: boolean = false;

  @Input()
  public readonly: boolean = false;

  @Input()
  public required: boolean = false;

  public quillConfig: QuillModules = {
    toolbar: {
      container: [
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    },
    clipboard: {
      /**
       * Important: when upgrading the quill.js library to v2, this option will be removed.
       * See {@link https://github.com/quilljs/quill/blob/ee827ffb605ba491246f201d497ce0e7d9e193a0/docs/guides/upgrading-to-2-0.md}
       * We will need to use helper {@link trimQuillHtmlEmptyLines} instead.
       */
      matchVisual: false,
    },
  };

  public value: string;

  public registerOnChange(fn: (value: string) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(value: string): void {
    this.value = value;
    this._cdr.markForCheck();
  }

  public onChange(value: string): void {
    this.value = value;
    this._onModelChange(value);
  }

  public onBlur(): void {
    this._onTouched();
  }

  private _onModelChange: (value: string) => any = () => {};

  private _onTouched: () => any = () => {};
}
